import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, Image, Loader2 } from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const ImageUpload = ({ value, onChange, className = "", shape = "rect" }) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo file non supportato. Usa JPEG, PNG, GIF o WebP.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File troppo grande. Max 5MB.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      onChange(response.data.url);
      toast.success("Immagine caricata!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const previewClasses = shape === "round"
    ? "w-32 h-32 rounded-full object-cover border border-slate-200 mx-auto"
    : shape === "square"
    ? "w-32 h-32 object-cover rounded-lg border border-slate-200 mx-auto"
    : "w-full h-32 object-cover rounded-lg border border-slate-200";

  const dropzoneClasses = shape === "round"
    ? "w-32 h-32 rounded-full border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors mx-auto"
    : shape === "square"
    ? "w-32 h-32 border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors mx-auto"
    : "w-full h-32 border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors";

  const overlayClasses = shape === "round"
    ? "absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center gap-2"
    : "absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2";

  return (
    <div className={`space-y-2 ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
      />

      {value ? (
        <div className={`relative group ${shape !== "rect" ? "w-32 mx-auto" : ""}`}>
          <img
            src={value.startsWith("http") ? value : `${API.replace("/api", "")}${value}`}
            alt="Preview"
            className={previewClasses}
          />
          <div className={overlayClasses}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="h-4 w-4 mr-1" />
              Cambia
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={dropzoneClasses}
        >
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 text-primary animate-spin mb-2" />
              <span className="text-sm text-slate-500">Caricamento...</span>
            </>
          ) : (
            <>
              <Image className="h-8 w-8 text-slate-400 mb-2" />
              <span className="text-sm text-slate-500">Clicca per caricare</span>
              {shape === "rect" && <span className="text-xs text-slate-400">JPEG, PNG, GIF, WebP (max 5MB)</span>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
